import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import type { PartialAgent } from "~/services/agents";
import { Markdown } from "./Markdown";
import { PulseDot } from "./PulseDot";
import { ToolCallsDisclosure } from "./ToolCallsDisclosure";
import type { AssistantTurnData } from "./useTurns";

interface AssistantTurnProps {
  turn: AssistantTurnData;
  agent: PartialAgent | null;
  // True between the user clicking Stop and the run ending. Only meaningful
  // on the in-flight turn (the one with `turn.isRunning`); changes the
  // status label from "Thinking" to "Stopping" so the chat area itself
  // reflects the pending cancel.
  isCancelling?: boolean;
}

// Renders one assistant turn as a single avatar plus a vertical stack:
//   - "Thinking…" / "Stopping…" status while the turn is in-flight
//   - collapsible "N tool calls" disclosure (when there are any)
//   - the final assistant text (the last text-message in the turn)
//   - "Response cancelled" marker when the turn ended via cancellation
//
// Multiple interim text messages within a turn are collapsed by the
// `useTurns` reducer — only the latest survives, matching the agent's
// intent (each prior text-message was a working thought it later revised).
export const AssistantTurn = ({ turn, agent, isCancelling }: AssistantTurnProps) => {
  const showThinking = turn.isRunning;
  const hasTools = turn.toolCalls.length > 0;
  const hasText = turn.text.length > 0;
  const showCancelled = turn.cancelled && !turn.isRunning;

  return (
    <li className="flex w-full items-start gap-3 self-start">
      <AgentAvatar id={agent?.id} name={agent?.name} size={28} aria-hidden="true" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {showThinking && (
          <div
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
            aria-live="polite"
          >
            <span>{isCancelling ? "Stopping" : "Thinking"}</span>
            <PulseDot />
          </div>
        )}
        {hasTools && <ToolCallsDisclosure entries={turn.toolCalls} isRunning={turn.isRunning} />}
        {hasText && (
          <div className="px-1 py-1 text-sm text-foreground">
            <Markdown text={turn.text} />
          </div>
        )}
        {showCancelled && (
          <div className="px-1 text-xs italic text-muted-foreground" aria-live="polite">
            Response cancelled
          </div>
        )}
      </div>
    </li>
  );
};
